/**
 * Logging Health Service
 * Centralized health monitoring for all logging services
 * Implements circuit breaker pattern and endpoint availability checks
 */

import { apiService } from './api';
import { adminTokenStorage } from './adminTokenStorage';

export interface LoggingServiceHealth {
  serviceName: string;
  isHealthy: boolean;
  circuitOpen: boolean;
  failures: number;
  consecutiveFailures: number;
  lastFailure?: number;
  lastSuccess?: number;
  lastHealthCheck?: number;
  endpointAvailable: boolean;
  responseTime?: number;
}

export interface HealthCheckResult {
  endpoint: string;
  available: boolean;
  responseTime: number;
  statusCode?: number;
  error?: string;
  timestamp: string;
}

export class LoggingHealthService {
  private readonly healthCheckInterval = 300000; // 5 minutes
  private readonly endpointTimeout = 5000; // 5 seconds
  private healthCheckTimer?: NodeJS.Timeout | undefined;
  private isRunning = false;
  private healthCheckInFlight = false;

  private readonly endpoints = [
    '/api/LogManagement/user-actions',
    '/api/LogManagement/api-logs',
    '/api/LogManagement/database-logs',
    '/api/LogManagement/error-logs'
  ];

  private readonly services = [
    'userActionLogger',
    'apiLogger',
    'databaseLogger',
    'errorLoggingService'
  ];

  constructor() {
    // Don't start health checks immediately - wait for admin authentication
    this.initializeHealthChecks();
  }

  /**
   * Initialize health checks - set up listeners for admin authentication
   */
  private initializeHealthChecks(): void {
    // Listen for admin login events to start health checks
    window.addEventListener('admin:login', () => {
      console.log('LoggingHealthService: Admin logged in, starting health checks after delay');
      // Add a delay to ensure admin login verification is complete
      setTimeout(() => {
        this.startHealthChecks();
      }, 1000); // 1 second delay to avoid race condition
    });

    // Listen for admin logout events to stop health checks
    window.addEventListener('admin:logout', () => {
      console.log('LoggingHealthService: Admin logged out, stopping health checks');
      this.stopHealthChecks();
    });

    // If admin session is valid (token + expiry via adminTokenStorage), start health checks
    if (adminTokenStorage.isAuthenticated()) {
      console.log('LoggingHealthService: Admin already authenticated, starting health checks');
      this.startHealthChecks();
    }
  }

  /**
   * Start periodic health checks
   */
  startHealthChecks(): void {
    if (this.isRunning) return;

    console.log('LoggingHealthService: Starting periodic health checks');
    this.isRunning = true;

    // Run initial health check
    this.performHealthChecks();

    // Schedule periodic health checks
    this.healthCheckTimer = setInterval(() => {
      this.performHealthChecks();
    }, this.healthCheckInterval);
  }

  /**
   * Stop periodic health checks
   */
  stopHealthChecks(): void {
    if (this.healthCheckTimer) {
      clearInterval(this.healthCheckTimer);
      this.healthCheckTimer = undefined;
    }
    this.isRunning = false;
    console.log('LoggingHealthService: Stopped periodic health checks');
  }

  /**
   * Perform comprehensive health checks for all logging services
   */
  async performHealthChecks(): Promise<void> {
    if (this.healthCheckInFlight) {
      return;
    }
    this.healthCheckInFlight = true;
    try {
      // Only perform health checks if admin token is valid (not merely present in localStorage)
      if (!adminTokenStorage.isAuthenticated()) {
        console.log('LoggingHealthService: Skipping health checks - no valid admin session');
        this.markServicesUnavailableDueToAuth();
        return;
      }

      console.log('LoggingHealthService: Performing health checks for all logging services');

      const endpointResults = await this.checkEndpointAvailability();

      // Server rejected credentials — stop polling to avoid 401 noise in DevTools
      if (endpointResults.some((r) => r.statusCode === 401)) {
        console.warn('LoggingHealthService: LogManagement returned 401; stopping health checks (re-login to admin if needed)');
        this.stopHealthChecks();
        return;
      }

      this.updateServiceHealthFromEndpoints(endpointResults);
      this.logHealthCheckResults(endpointResults);
      this.updateCircuitBreakers();
    } catch (error) {
      console.error('LoggingHealthService: Error during health checks:', error);
    } finally {
      this.healthCheckInFlight = false;
    }
  }

  /**
   * Check availability of all LogManagement endpoints
   */
  async checkEndpointAvailability(): Promise<HealthCheckResult[]> {
    const results: HealthCheckResult[] = [];

    for (const endpoint of this.endpoints) {
      const result = await this.checkSingleEndpoint(endpoint);
      results.push(result);
    }

    return results;
  }

  /**
   * Check availability of a single endpoint
   */
  async checkSingleEndpoint(endpoint: string): Promise<HealthCheckResult> {
    const startTime = Date.now();
    
    try {
      const adminToken = adminTokenStorage.getToken();
      if (!adminToken) {
        return {
          endpoint,
          available: false,
          responseTime: 0,
          statusCode: 401,
          error: 'No valid admin authentication for health check',
          timestamp: new Date().toISOString()
        };
      }

      apiService.setAdminToken(adminToken);

      // Use GET request to check availability with proper authentication
      const response = await Promise.race([
        apiService.get(endpoint + '?healthCheck=true', { 
          timeout: this.endpointTimeout,
          cache: false 
        }),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Timeout')), this.endpointTimeout)
        )
      ]) as any;

      const responseTime = Date.now() - startTime;

      return {
        endpoint,
        available: response.status >= 200 && response.status < 500, // Accept 4xx as "available but not working"
        responseTime,
        statusCode: response.status,
        timestamp: new Date().toISOString()
      };
    } catch (error: any) {
      const responseTime = Date.now() - startTime;
      const statusCode = error?.response?.status;

      // If it's a 401, it means the endpoint is available but authentication failed
      const available = statusCode === 401;

      return {
        endpoint,
        available,
        responseTime,
        statusCode,
        error: error?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      };
    }
  }

  /**
   * Update service health based on endpoint availability
   */
  private updateServiceHealthFromEndpoints(endpointResults: HealthCheckResult[]): void {
    const endpointMap = {
      '/api/LogManagement/user-actions': 'userActionLogger',
      '/api/LogManagement/database-logs': 'databaseLogger',
      '/api/LogManagement/error-logs': 'errorLoggingService'
    };

    for (const result of endpointResults) {
      const serviceName = endpointMap[result.endpoint as keyof typeof endpointMap];
      if (serviceName) {
        this.updateServiceEndpointHealth(serviceName, result);
      }
    }
  }

  /**
   * Update individual service endpoint health
   */
  private updateServiceEndpointHealth(serviceName: string, result: HealthCheckResult): void {
    try {
      const healthKey = `${serviceName}Health`;
      const healthData = JSON.parse(localStorage.getItem(healthKey) || '{}');

      // Update endpoint availability
      healthData.endpointAvailable = result.available;
      healthData.lastHealthCheck = Date.now();
      healthData.responseTime = result.responseTime;

      // If endpoint is available, it might help with circuit breaker decisions
      if (result.available && healthData.circuitOpen) {
        // Don't automatically close circuit breaker, but note that endpoint is available
        healthData.endpointAvailableWhileCircuitOpen = true;
      }

      localStorage.setItem(healthKey, JSON.stringify(healthData));
    } catch (error) {
      console.error(`LoggingHealthService: Failed to update health for ${serviceName}:`, error);
    }
  }

  /**
   * Log health check results
   */
  private logHealthCheckResults(results: HealthCheckResult[]): void {
    const summary = {
      timestamp: new Date().toISOString(),
      totalEndpoints: results.length,
      availableEndpoints: results.filter(r => r.available).length,
      averageResponseTime: results.reduce((sum, r) => sum + r.responseTime, 0) / results.length,
      results: results.map(r => ({
        endpoint: r.endpoint,
        available: r.available,
        responseTime: r.responseTime,
        statusCode: r.statusCode
      }))
    };

    console.log('LoggingHealthService: Health check summary:', summary);

    // Store health check history
    this.storeHealthCheckHistory(summary);
  }

  /**
   * Store health check history for analysis
   */
  private storeHealthCheckHistory(summary: any): void {
    try {
      const history = this.getHealthCheckHistory();
      history.push(summary);

      // Keep only the last 24 health checks (2 hours at 5-minute intervals)
      if (history.length > 24) {
        history.splice(0, history.length - 24);
      }

      localStorage.setItem('loggingHealthHistory', JSON.stringify(history));
    } catch (error) {
      console.error('LoggingHealthService: Failed to store health check history:', error);
    }
  }

  /**
   * Get health check history
   */
  getHealthCheckHistory(): any[] {
    try {
      return JSON.parse(localStorage.getItem('loggingHealthHistory') || '[]');
    } catch {
      return [];
    }
  }

  /**
   * Mark all services as unavailable due to lack of authentication
   */
  private markServicesUnavailableDueToAuth(): void {
    for (const serviceName of this.services) {
      try {
        const healthKey = `${serviceName}Health`;
        const healthData = JSON.parse(localStorage.getItem(healthKey) || '{}');
        
        healthData.endpointAvailable = false;
        healthData.lastHealthCheck = Date.now();
        healthData.authenticationRequired = true;
        healthData.responseTime = 0;
        
        localStorage.setItem(healthKey, JSON.stringify(healthData));
      } catch (error) {
        console.error(`LoggingHealthService: Failed to mark ${serviceName} as unavailable due to auth:`, error);
      }
    }
  }

  /**
   * Update circuit breakers based on current health status
   */
  private updateCircuitBreakers(): void {
    for (const serviceName of this.services) {
      try {
        const health = this.getServiceHealth(serviceName);
        
        // If service has been failing and endpoint is now available, consider half-open state
        if (health.circuitOpen && health.endpointAvailable && health.consecutiveFailures < 10) {
          this.setCircuitBreakerHalfOpen(serviceName);
        }
        
        // If endpoint is consistently unavailable, ensure circuit breaker is open
        if (!health.endpointAvailable && !health.circuitOpen && health.failures > 3) {
          this.openCircuitBreaker(serviceName, 'Endpoint unavailable during health check');
        }
      } catch (error) {
        console.error(`LoggingHealthService: Failed to update circuit breaker for ${serviceName}:`, error);
      }
    }
  }

  /**
   * Set circuit breaker to half-open state
   */
  private setCircuitBreakerHalfOpen(serviceName: string): void {
    try {
      const healthKey = `${serviceName}Health`;
      const healthData = JSON.parse(localStorage.getItem(healthKey) || '{}');
      
      healthData.circuitHalfOpen = true;
      healthData.halfOpenAt = Date.now();
      
      localStorage.setItem(healthKey, JSON.stringify(healthData));
      console.log(`LoggingHealthService: Set ${serviceName} circuit breaker to half-open state`);
    } catch (error) {
      console.error(`LoggingHealthService: Failed to set half-open state for ${serviceName}:`, error);
    }
  }

  /**
   * Open circuit breaker for a service
   */
  private openCircuitBreaker(serviceName: string, reason: string): void {
    try {
      const healthKey = `${serviceName}Health`;
      const healthData = JSON.parse(localStorage.getItem(healthKey) || '{}');
      
      healthData.circuitOpen = true;
      healthData.circuitOpenedAt = Date.now();
      healthData.circuitOpenReason = reason;
      
      localStorage.setItem(healthKey, JSON.stringify(healthData));
      console.warn(`LoggingHealthService: Opened ${serviceName} circuit breaker - ${reason}`);
    } catch (error) {
      console.error(`LoggingHealthService: Failed to open circuit breaker for ${serviceName}:`, error);
    }
  }

  /**
   * Get health status for a specific service
   */
  getServiceHealth(serviceName: string): LoggingServiceHealth {
    try {
      const healthKey = `${serviceName}Health`;
      const healthData = JSON.parse(localStorage.getItem(healthKey) || '{}');
      
      return {
        serviceName,
        isHealthy: !healthData.circuitOpen && (healthData.failures || 0) < 5 && (healthData.endpointAvailable !== false),
        circuitOpen: healthData.circuitOpen || false,
        failures: healthData.failures || 0,
        consecutiveFailures: healthData.consecutiveFailures || 0,
        lastFailure: healthData.lastFailure,
        lastSuccess: healthData.lastSuccess,
        lastHealthCheck: healthData.lastHealthCheck,
        endpointAvailable: healthData.endpointAvailable !== false, // Default to true if not set
        responseTime: healthData.responseTime
      };
    } catch {
      return {
        serviceName,
        isHealthy: true,
        circuitOpen: false,
        failures: 0,
        consecutiveFailures: 0,
        endpointAvailable: true
      };
    }
  }

  /**
   * Get health status for all services
   */
  getAllServicesHealth(): LoggingServiceHealth[] {
    return this.services.map(serviceName => this.getServiceHealth(serviceName));
  }

  /**
   * Get overall logging system health
   */
  getOverallHealth(): {
    isHealthy: boolean;
    healthyServices: number;
    totalServices: number;
    availableEndpoints: number;
    totalEndpoints: number;
    services: LoggingServiceHealth[];
  } {
    const services = this.getAllServicesHealth();
    const healthyServices = services.filter(s => s.isHealthy).length;
    const availableEndpoints = services.filter(s => s.endpointAvailable).length;
    
    return {
      isHealthy: healthyServices >= Math.ceil(services.length * 0.5), // At least 50% healthy
      healthyServices,
      totalServices: services.length,
      availableEndpoints,
      totalEndpoints: this.endpoints.length,
      services
    };
  }

  /**
   * Force health check for all services
   */
  async forceHealthCheck(): Promise<void> {
    console.log('LoggingHealthService: Forcing immediate health check');
    await this.performHealthChecks();
  }

  /**
   * Reset all circuit breakers (for admin use)
   */
  resetAllCircuitBreakers(): void {
    console.log('LoggingHealthService: Resetting all circuit breakers');
    
    for (const serviceName of this.services) {
      try {
        const healthKey = `${serviceName}Health`;
        const healthData = JSON.parse(localStorage.getItem(healthKey) || '{}');
        
        healthData.circuitOpen = false;
        healthData.circuitHalfOpen = false;
        healthData.failures = 0;
        healthData.consecutiveFailures = 0;
        healthData.lastReset = Date.now();
        
        localStorage.setItem(healthKey, JSON.stringify(healthData));
      } catch (error) {
        console.error(`LoggingHealthService: Failed to reset circuit breaker for ${serviceName}:`, error);
      }
    }
  }

  /**
   * Check if a specific endpoint is available before making a request
   */
  async isEndpointAvailable(endpoint: string): Promise<boolean> {
    try {
      const result = await this.checkSingleEndpoint(endpoint);
      return result.available;
    } catch {
      return false;
    }
  }

  /**
   * Get graceful degradation recommendations
   */
  getGracefulDegradationStatus(): {
    shouldDisableLogging: boolean;
    shouldUseBatchMode: boolean;
    shouldUseOfflineMode: boolean;
    recommendations: string[];
  } {
    const overallHealth = this.getOverallHealth();
    const recommendations: string[] = [];
    
    let shouldDisableLogging = false;
    let shouldUseBatchMode = false;
    let shouldUseOfflineMode = false;
    
    if (overallHealth.availableEndpoints === 0) {
      shouldUseOfflineMode = true;
      recommendations.push('All endpoints unavailable - switch to offline mode');
    } else if (overallHealth.healthyServices < 2) {
      shouldUseBatchMode = true;
      recommendations.push('Limited service availability - use batch mode');
    }
    
    if (overallHealth.healthyServices === 0) {
      shouldDisableLogging = true;
      recommendations.push('No healthy services - consider disabling logging temporarily');
    }
    
    return {
      shouldDisableLogging,
      shouldUseBatchMode,
      shouldUseOfflineMode,
      recommendations
    };
  }

  /**
   * Cleanup resources
   */
  destroy(): void {
    this.stopHealthChecks();
  }
}

// Export singleton instance
export const loggingHealthService = new LoggingHealthService();

export default LoggingHealthService;